import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import JsonLd, { homeSchema } from '../components/JsonLd';
import InteractiveChart from '../components/InteractiveChart';

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="7" fill="#1D9E75" fillOpacity="0.15"/>
    <path d="M4.5 7l1.8 1.8L9.5 5.5" stroke="#1D9E75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const pillars = [
  {
    title: 'Stock Market Fundamentals',
    desc: 'What the market is, how it works, and who participates in it.',
    topics: ['What is the stock market?', 'Market participants', 'Stock exchanges & IPOs'],
    link: '/stock-market-fundamentals',
    bg: '#E1F5EE',
    icon: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
        <path d="M4 18l5-5 4 4 5-6 4-4" stroke="#1D9E75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="4" cy="18" r="2" fill="#1D9E75"/>
        <circle cx="9" cy="13" r="2" fill="#1D9E75"/>
        <circle cx="13" cy="17" r="2" fill="#1D9E75"/>
        <circle cx="18" cy="11" r="2" fill="#1D9E75"/>
        <circle cx="22" cy="7" r="2" fill="#1D9E75"/>
      </svg>
    ),
  },
  {
    title: 'Investment Basics',
    desc: 'Stocks, bonds, ETFs, and how to build a portfolio that fits your goals.',
    topics: ['Types of investments', 'Asset allocation', 'Compound interest & DCA'],
    link: '/investment-basics',
    bg: '#EAF3DE',
    icon: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
        <circle cx="13" cy="13" r="9" stroke="#639922" strokeWidth="2"/>
        <path d="M13 8v5l3 3" stroke="#639922" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: 'Trading Principles',
    desc: 'Order types, chart patterns, and the psychology behind good decisions.',
    topics: ['Market vs limit orders', 'Chart patterns', 'Trading psychology'],
    link: '/trading-principles',
    bg: '#FAEEDA',
    icon: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
        <rect x="3" y="6" width="20" height="15" rx="3" stroke="#BA7517" strokeWidth="2"/>
        <path d="M7 16l3-4 3 3 4-5 3 2" stroke="#BA7517" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: 'Risk Management',
    desc: 'Protect your capital with diversification, position sizing, and stop-losses.',
    topics: ['Diversification strategies', 'Position sizing', 'Stop-loss techniques'],
    link: '/risk-management',
    bg: '#FAECE7',
    icon: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
        <path d="M13 3l8 4v6c0 5-8 10-8 10S5 18 5 13V7l8-4z" stroke="#D85A30" strokeWidth="2" strokeLinejoin="round"/>
        <path d="M10 13l2 2 4-4" stroke="#D85A30" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: 'Market Analysis',
    desc: 'Fundamental and technical analysis — how to read a company and a chart.',
    topics: ['Fundamental analysis', 'Technical indicators', 'Economic indicators'],
    link: '/market-analysis',
    bg: '#EEEDFE',
    icon: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="7" stroke="#7F77DD" strokeWidth="2"/>
        <path d="M17 17l4 4" stroke="#7F77DD" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: 'Trading Platforms',
    desc: 'Compare brokers side-by-side and find the right one for your style.',
    topics: ['Robinhood vs Fidelity vs Schwab', 'Fees & features', 'Best for beginners'],
    link: '/trading-platforms',
    bg: '#F1EFE8',
    icon: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
        <rect x="4" y="7" width="18" height="13" rx="3" stroke="#5F5E5A" strokeWidth="2"/>
        <path d="M9 22h8M13 20v2" stroke="#5F5E5A" strokeWidth="2" strokeLinecap="round"/>
        <path d="M8 12h10M8 15h6" stroke="#5F5E5A" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
];

const whyItems = [
  {
    num: '01',
    title: 'Beginner-friendly language',
    desc: 'Every concept explained in plain English with a real-world example alongside it.',
  },
  {
    num: '02',
    title: 'Learn by doing',
    desc: 'Quizzes after each section reinforce what you read, not just what you remember.',
  },
  {
    num: '03',
    title: 'Everything in one place',
    desc: 'From your first trade to reading an earnings report — the full beginner curriculum.',
  },
];

const Home = () => {
  return (
    <div>
      <SEO
        title="StckFuture - Free Stock Market Training for Beginners"
        description="Learn stock market investing for free. Master fundamentals, trading principles, risk management, and market analysis with beginner-friendly guides and quizzes."
        canonical="https://www.stckfuture.com/"
      />
      <JsonLd schema={homeSchema} />

      {/* ── HERO ── */}
      <section className="sf-hero">
        <div className="container">
          <div className="sf-hero-badge">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M2 9l2.5-3L6.5 8 9 5l2 1.5" stroke="#9FE1CB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Free stock market education
          </div>

          <h1>
            Learn to invest<br />
            <span className="sf-accent">without the jargon</span>
          </h1>

          <p className="sf-hero-sub">
            StckFuture teaches beginners how the stock market works — from your first share to
            building a real portfolio. Step by step, no experience needed.
          </p>

          <div className="sf-hero-actions">
            <Link to="/stock-market-fundamentals" className="btn-sf-primary">
              Start with the basics
            </Link>
            <Link to="/quiz" className="btn-sf-ghost">
              Test your knowledge
            </Link>
          </div>

          <div className="sf-hero-stats">
            <div>
              <span className="sf-stat-num">6</span>
              <div className="sf-stat-label">Learning paths</div>
            </div>
            <div>
              <span className="sf-stat-num">70+</span>
              <div className="sf-stat-label">Terms explained</div>
            </div>
            <div>
              <span className="sf-stat-num">50+</span>
              <div className="sf-stat-label">Quiz questions</div>
            </div>
            <div>
              <span className="sf-stat-num">Free</span>
              <div className="sf-stat-label">Always</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ── */}
      <div className="sf-trust-bar">
        <div className="container">
          <div className="sf-trust-bar-inner">
            <div className="sf-trust-item">
              <CheckIcon />
              Beginner friendly
            </div>
            <div className="sf-trust-sep"></div>
            <div className="sf-trust-item">
              <CheckIcon />
              Real market examples
            </div>
            <div className="sf-trust-sep"></div>
            <div className="sf-trust-item">
              <CheckIcon />
              No sign-up needed
            </div>
            <div className="sf-trust-sep"></div>
            <div className="sf-trust-item">
              <CheckIcon />
              Quiz yourself as you go
            </div>
          </div>
        </div>
      </div>

      {/* ── LEARNING PATHS ── */}
      <section className="sf-section">
        <div className="container">
          <div className="text-center mb-4">
            <span className="sf-eyebrow">What you'll learn</span>
            <h2 className="sf-section-heading">Choose where to start</h2>
            <p className="sf-section-sub mx-auto">
              Six focused modules, each with real examples and a quiz to lock in the knowledge.
            </p>
          </div>

          <div className="sf-pillars-grid">
            {pillars.map((p) => (
              <Link key={p.link} to={p.link} className="sf-pillar-card">
                <div
                  className="sf-pillar-icon-wrap"
                  style={{ background: p.bg }}
                >
                  {p.icon}
                </div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                <ul className="sf-pillar-topics">
                  {p.topics.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── MARKET CHART ── */}
      <section className="sf-section sf-section-alt">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <div className="text-center mb-4">
                <span className="sf-eyebrow">Interactive demo</span>
                <h2 className="sf-section-heading">See market growth in action</h2>
                <p className="sf-section-sub mx-auto">
                  Hover over data points to explore how a hypothetical investment grows over time.
                </p>
              </div>
              <div className="sf-chart-wrap">
                <InteractiveChart />
              </div>
              <p className="text-center sf-text-muted sf-text-small mt-3">
                Simplified example for educational purposes. Past performance does not guarantee future results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY STCKFUTURE ── */}
      <section className="sf-section">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-5">
              <span className="sf-eyebrow">Why StckFuture</span>
              <h2 className="sf-section-heading">Built for people starting from zero</h2>
              <p className="sf-section-sub mt-2">
                Most investing content assumes you already know the basics. We don't.
                Every guide here starts from scratch and builds up deliberately.
              </p>
              <div className="mt-4">
                <Link to="/stock-market-fundamentals" className="btn-sf-primary">
                  Begin learning
                </Link>
              </div>
            </div>

            <div className="col-lg-7">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {whyItems.map((item) => (
                  <div
                    key={item.num}
                    style={{
                      display: 'flex',
                      gap: '20px',
                      alignItems: 'flex-start',
                      background: 'var(--off-white)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-lg)',
                      padding: '20px 24px',
                    }}
                  >
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: 'var(--green)',
                      fontVariantNumeric: 'tabular-nums',
                      paddingTop: '2px',
                      flexShrink: 0,
                    }}>
                      {item.num}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9375rem', marginBottom: '4px', color: 'var(--text-primary)' }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section
        style={{
          background: 'var(--navy)',
          padding: '64px 0',
          textAlign: 'center',
        }}
      >
        <div className="container">
          <h2 style={{ color: 'var(--white)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '12px' }}>
            Ready to start your investment journey?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1.0625rem', marginBottom: '28px', maxWidth: 440, margin: '0 auto 28px' }}>
            Pick a module, read through, quiz yourself, and move on to the next.
            The whole curriculum is free.
          </p>
          <Link to="/stock-market-fundamentals" className="btn-sf-primary">
            Begin with the fundamentals
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
